import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import { BrowserRouter as Router, useParams, useHistory, Route, Switch } from "react-router-dom";
import "../css/Admin_css/admin-customize.css";
import myToast from "../Utils/MyToast";
import COLORS from "../resources/colors/COLORS";
import BackButton from "../ChildComponent/BackButton";
import AddExamSocialValueField from "./AddExamSocialValueField";
import MdlSelectExam from "../dialog/MdlSelectExam";

// ============================================================================
// CONSTANTS
// ============================================================================

const API_ENDPOINTS = {
  GET_FIELDS: "getExamSocialValueFieldMaster.php",
  ADD_FIELD: "AddExamSocialValueField.php",
  UPDATE_FIELD: "UpdateExamSocialValueField.php",
  DELETE_FIELD: "DeleteExamSocialValueField.php",
  UPDATE_ORDER: "UpdateSocialValueFieldOrder.php",
};

// ============================================================================
// UTILITIES
// ============================================================================

const buildApiUrl = (functionName) => {
  return `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}${functionName}`;
};

const sortFieldsByOrder = (fields) => {
  return [...fields].sort((a, b) => {
    if (a.field_order !== undefined && b.field_order !== undefined) {
      return (a.field_order || 0) - (b.field_order || 0);
    }
    return (a.id || 0) - (b.id || 0);
  });
};

// ============================================================================
// API SERVICE FUNCTIONS
// ============================================================================

const fetchSocialValueFields = async (examId, sessionId) => {
  const schoolCode = SessionManager.getSchoolCode();
  const url = buildApiUrl(API_ENDPOINTS.GET_FIELDS);
  const params = {
    exam_id: examId,
    school_code: schoolCode,
    session_id: sessionId,
  };

  try {
    const response = await axios.get(url, { params });
    if (response.data?.api_status === "OK") {
      const sortedFields = sortFieldsByOrder(response.data.data || []);
      return { success: true, data: sortedFields };
    }
    return { success: false, data: [], message: response.data?.api_remark || "Failed to load fields" };
  } catch (error) {
    console.error("Error fetching social value fields:", error);
    return { success: false, data: [], message: "Error loading fields" };
  }
};

const addSocialValueField = async (examId, sessionId, fieldName) => {
  const schoolCode = SessionManager.getSchoolCode();
  const url = buildApiUrl(API_ENDPOINTS.ADD_FIELD);
  const params = {
    exam_id: String(examId),
    session_id: String(sessionId),
    school_code: String(schoolCode),
    field_name: String(fieldName),
  };

  try {
    const response = await axios.get(url, { params });
    if (response.data?.api_status === "OK") {
      return { success: true, message: response.data?.api_remark || "Field added successfully" };
    }
    return { success: false, message: response.data?.api_remark || "Failed to add field" };
  } catch (error) {
    console.error("Error adding field:", error);
    return { success: false, message: "Error adding field" };
  }
};

const updateSocialValueField = async (examId, sessionId, fieldId, fieldName) => {
  const schoolCode = SessionManager.getSchoolCode();
  const staffDetails = SessionManager.getStaffDetails();
  const staffId = staffDetails?.staff_id || "";
  const url = buildApiUrl(API_ENDPOINTS.UPDATE_FIELD);
  const params = {
    staff_id: String(staffId),
    field_id: String(fieldId),
    exam_id: String(examId),
    session_id: String(sessionId),
    school_code: String(schoolCode),
    field_name: String(fieldName),
  };

  try {
    const response = await axios.get(url, { params });
    if (response.data?.api_status === "OK") {
      return { success: true, message: response.data?.api_remark || "Field updated successfully" };
    }
    return { success: false, message: response.data?.api_remark || "Failed to update field" };
  } catch (error) {
    console.error("Error updating field:", error);
    return { success: false, message: "Error updating field" };
  }
};

const deleteSocialValueField = async (examId, sessionId, fieldId) => {
  const schoolCode = SessionManager.getSchoolCode();
  const staffDetails = SessionManager.getStaffDetails();
  const staffId = staffDetails?.staff_id || "";
  const url = buildApiUrl(API_ENDPOINTS.DELETE_FIELD);
  const params = {
    staff_id: String(staffId),
    field_id: String(fieldId),
    exam_id: String(examId),
    session_id: String(sessionId),
    school_code: String(schoolCode),
  };

  try {
    const response = await axios.get(url, { params });
    if (response.data?.api_status === "OK") {
      return { success: true, message: response.data?.api_remark || "Field deleted successfully" };
    }
    return { success: false, message: response.data?.api_remark || "Failed to delete field" };
  } catch (error) {
    console.error("Error deleting field:", error);
    return { success: false, message: "Error deleting field" };
  }
};

const updateFieldOrder = async (examId, sessionId, fieldOrder) => {
  const schoolCode = SessionManager.getSchoolCode();
  const url = buildApiUrl(API_ENDPOINTS.UPDATE_ORDER);
  const params = {
    exam_id: examId,
    session_id: sessionId,
    school_code: schoolCode,
    field_order: JSON.stringify(fieldOrder),
  };

  try {
    const response = await axios.get(url, { params });
    if (response.data?.api_status === "OK") {
      return { success: true, message: response.data?.api_remark || "Order updated successfully" };
    }
    return { success: false, message: response.data?.api_remark || "Failed to update order" };
  } catch (error) {
    console.error("Error updating order:", error);
    return { success: false, message: "Error updating order" };
  }
};

// ============================================================================
// MAIN (ROUTER WRAPPER)
// ============================================================================

const ManageSocialValueField = () => {
  return (
    <Router>
      <Switch>
        <Route
          path="/AdminMain/ManageSocialValueField/:exam_id/:session_id/:exam_name/:exam_status"
          exact
        >
          <ManageSocialValueFieldContent />
        </Route>
        <Route
          path="/AdminMain/AddExamSocialValueField/:exam_id/:session_id/:exam_name"
          component={AddExamSocialValueField}
        />
      </Switch>
    </Router>
  );
};

// ============================================================================
// CONTENT
// ============================================================================

const ManageSocialValueFieldContent = () => {
  const { exam_id, session_id, exam_name, exam_status } = useParams();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fields, setFields] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newFieldName, setNewFieldName] = useState("");
  const [editingField, setEditingField] = useState(null);
  const [editFieldName, setEditFieldName] = useState("");
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [updatingOrder, setUpdatingOrder] = useState(false);
  const [showExamDialog, setShowExamDialog] = useState(false);

  const EXAM_ID = exam_id || "";
  const SESSION_ID = session_id || "";
  const EXAM_NAME = decodeURIComponent(exam_name || "");
  const EXAM_STATUS = exam_status || "";

  const fetchData = useCallback(async () => {
    if (!EXAM_ID || !SESSION_ID || EXAM_ID === ":exam_id") {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const result = await fetchSocialValueFields(EXAM_ID, SESSION_ID);
      if (result.success) {
        setFields(result.data);
      } else {
        myToast.notify_warning(result.message);
        setFields([]);
      }
    } catch (error) {
      console.error("Error fetching social value fields:", error);
      myToast.notify_error("Failed to load social value fields");
      setFields([]);
    } finally {
      setLoading(false);
    }
  }, [EXAM_ID, SESSION_ID]);

  // Check if exam is selected, if not show dialog immediately
  useEffect(() => {
    if (!EXAM_ID || EXAM_ID === "" || EXAM_ID === ":exam_id") {
      setShowExamDialog(true);
      setLoading(false); // Don't show loader when waiting for exam selection
      return;
    }
    // Only fetch data if exam is selected
    fetchData();
  }, [EXAM_ID, SESSION_ID, fetchData]);

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  const handleCloseExamDialog = () => {
    setShowExamDialog(false);
  };

  const handleSelectExam = (selectedExamId, selectedExamName) => {
    const currentSession = SessionManager.getCurrentSession();
    const sessionId = currentSession?.[0]?.session_id || SESSION_ID || "";
    const sessionStatus = currentSession?.[0]?.status || "Active";
    
    // Navigate to the same route with selected exam
    history.push(
      `/AdminMain/ManageSocialValueField/${selectedExamId}/${sessionId}/${encodeURIComponent(selectedExamName)}/${sessionStatus}`
    );
    setShowExamDialog(false);
  };

  const handleChangeExam = () => {
    setShowExamDialog(true);
  };

  const handleAddField = async () => {
    if (!newFieldName.trim()) {
      myToast.notify_warning("Please enter a field name");
      return;
    }
    setSaving(true);
    try {
      const result = await addSocialValueField(EXAM_ID, SESSION_ID, newFieldName.trim());
      if (result.success) {
        myToast.notify_success(result.message);
        setNewFieldName("");
        setShowAddForm(false);
        await fetchData();
      } else {
        myToast.notify_error(result.message);
      }
    } catch (error) {
      console.error("Error adding field:", error);
      myToast.notify_error("Failed to add field");
    } finally {
      setSaving(false);
    }
  };

  const handleEditField = (field) => {
    if (!field || !field.field_id) return;
    setEditingField(field);
    setEditFieldName(field.field_name || "");
  };

  const handleUpdateField = async () => {
    if (!editingField) return;
    if (!editFieldName.trim()) {
      myToast.notify_warning("Please enter a field name");
      return;
    }
    setSaving(true);
    try {
      const result = await updateSocialValueField(EXAM_ID, SESSION_ID, editingField.field_id, editFieldName.trim());
      if (result.success) {
        myToast.notify_success(result.message);
        setEditingField(null);
        setEditFieldName("");
        await fetchData();
      } else {
        myToast.notify_error(result.message);
      }
    } catch (error) {
      console.error("Error updating field:", error);
      myToast.notify_error("Failed to update field");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteField = async (field) => {
    if (!window.confirm(`Are you sure you want to delete "${field.field_name}"?`)) return;
    setSaving(true);
    try {
      const result = await deleteSocialValueField(EXAM_ID, SESSION_ID, field.field_id);
      if (result.success) {
        myToast.notify_success(result.message);
        await fetchData();
      } else {
        myToast.notify_error(result.message);
      }
    } catch (error) {
      console.error("Error deleting field:", error);
      myToast.notify_error("Failed to delete field");
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingField(null);
    setEditFieldName("");
  };

  const handleDragStart = (index) => setDraggedIndex(index);
  const handleDragOver = (e) => e.preventDefault();

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }
    const newFields = [...fields];
    const draggedItem = newFields[draggedIndex];
    newFields.splice(draggedIndex, 1);
    newFields.splice(dropIndex, 0, draggedItem);

    const fieldOrder = newFields.map((field, index) => ({
      field_id: field.field_id,
      field_order: index + 1,
    }));

    setUpdatingOrder(true);
    try {
      const result = await updateFieldOrder(EXAM_ID, SESSION_ID, fieldOrder);
      if (result.success) {
        setFields(newFields);
        myToast.notify_success(result.message);
      } else {
        myToast.notify_error(result.message);
        await fetchData();
      }
    } catch (error) {
      console.error("Error updating order:", error);
      myToast.notify_error("Failed to update field order");
      await fetchData();
    } finally {
      setUpdatingOrder(false);
      setDraggedIndex(null);
    }
  };

  const handleDragEnd = () => setDraggedIndex(null);

  // ============================================================================
  // RENDER
  // ============================================================================
  // Show modal immediately if no exam selected, don't block with loader
  if (showExamDialog) {
    return (
      <div className="content-wrapper">
        <MdlSelectExam
          action_close={handleCloseExamDialog}
          action_select={handleSelectExam}
        />
      </div>
    );
  }

  // Don't show loader if we're waiting for exam selection
  if (loading && !showExamDialog) {
    return <MyLoader />;
  }

  return (
    <div className="content-wrapper">
      <div className="content">
        <div className="container-fluid">
          <div className="content-header">
            <div className="leftside-content-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px" }}>
              <ul className="breadcrumbs">
                <li>
                  <i className="fa fa-home" aria-hidden="true" />
                  <a href="#">Manage Social Value Field</a>
                </li>
              </ul>
              <BackButton 
                onClick={() => window.history.back()} 
                style={{ height: "28px", fontSize: "11px", padding: "2px 6px", marginTop: "0px" }}
              />
            </div>
          </div>

          <div
            className="bg_page_white_rounded box_shadow"
            style={{
              margin: "20px 40px",
              padding: "0",
              borderRadius: "16px",
              overflow: "hidden",
              background: "#ffffff",
              boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
            }}
          >
            <div
              style={{
                background: `linear-gradient(135deg, ${COLORS.primary_color_shade5 || "#3b82f6"} 0%, ${COLORS.primary_color_shade5 || "#2563eb"} 100%)`,
                padding: "16px 20px",
                color: "#ffffff",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
              >
                <div>
                  <h2
                    style={{
                      margin: 0,
                      fontSize: "20px",
                      fontWeight: "700",
                      color: "#ffffff",
                      letterSpacing: "-0.3px",
                    }}
                  >
                    Manage Social Value Field
                  </h2>
                  <p
                    onClick={handleChangeExam}
                    style={{
                      margin: "4px 0 0 0",
                      fontSize: "12px",
                      color: "rgba(255, 255, 255, 0.9)",
                      fontWeight: "400",
                      cursor: "pointer",
                      display: "inline-block",
                      padding: "4px 8px",
                      borderRadius: "4px",
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = "rgba(255, 255, 255, 0.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = "transparent";
                    }}
                  >
                    <i className="fa fa-graduation-cap" style={{ marginRight: "6px" }}></i>
                    Exam: {EXAM_NAME || "Click to select"}
                    <i className="fa fa-edit" style={{ marginLeft: "6px", fontSize: "10px" }}></i>
                  </p>
                </div>
                <div>
                  <button
                    style={{
                      background: "rgba(255, 255, 255, 0.2)",
                      color: "#ffffff",
                      border: "2px solid rgba(255, 255, 255, 0.3)",
                      borderRadius: "8px",
                      padding: "8px 16px",
                      fontSize: "12px",
                      fontWeight: "600",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = "rgba(255, 255, 255, 0.3)";
                      e.target.style.transform = "translateY(-2px)";
                      e.target.style.boxShadow = "0 4px 8px rgba(0, 0, 0, 0.15)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = "rgba(255, 255, 255, 0.2)";
                      e.target.style.transform = "translateY(0)";
                      e.target.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.1)";
                    }}
                    onClick={() =>
                      history.push(
                        `/AdminMain/AddExamSocialValueField/${EXAM_ID}/${SESSION_ID}/${encodeURIComponent(EXAM_NAME)}`
                      )
                    }
                  >
                    <i className="fa fa-plus-circle"></i>
                    Add Mark And Grade
                  </button>
                </div>
              </div>
            </div>

            <div style={{ padding: "16px 20px" }}>
              {showAddForm && (
                <div
                  style={{
                    border: "2px dashed #e5e7eb",
                    borderRadius: "8px",
                    padding: "12px 16px",
                    marginBottom: "16px",
                    background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                    transition: "all 0.3s ease",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
                    <div
                      style={{
                        width: "32px",
                        height: "32px",
                        borderRadius: "8px",
                        background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#fff",
                        fontSize: "14px",
                      }}
                    >
                      <i className="fa fa-plus"></i>
                    </div>
                    <h3
                      style={{ margin: 0, fontSize: "14px", fontWeight: "700", color: "#111827" }}
                    >
                      Add New Field
                    </h3>
                  </div>
                  <div style={{ display: "flex", gap: "8px", alignItems: "stretch" }}>
                    <input
                      type="text"
                      value={newFieldName}
                      onChange={(e) => setNewFieldName(e.target.value)}
                      placeholder="Enter field name"
                      style={{
                        flex: 1,
                        padding: "8px 12px",
                        border: "2px solid #e5e7eb",
                        borderRadius: "8px",
                        fontSize: "12px",
                        background: "#ffffff",
                        transition: "all 0.2s ease",
                        outline: "none",
                      }}
                    />
                    <button
                      onClick={handleAddField}
                      disabled={saving}
                      style={{
                        background: saving ? "#9ca3af" : "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                        color: "#fff",
                        border: "none",
                        borderRadius: "8px",
                        padding: "8px 16px",
                        fontSize: "12px",
                        fontWeight: "600",
                        cursor: saving ? "not-allowed" : "pointer",
                        transition: "all 0.3s ease",
                        boxShadow: "0 4px 6px rgba(16, 185, 129, 0.3)",
                      }}
                    >
                      {saving ? <><i className="fa fa-spinner fa-spin" style={{ marginRight: "4px" }}></i>Adding...</> : <><i className="fa fa-check" style={{ marginRight: "4px" }}></i>Add Field</>}
                    </button>
                    <button
                      onClick={() => { setShowAddForm(false); setNewFieldName(""); }}
                      style={{
                        background: "#ffffff",
                        color: "#6b7280",
                        border: "2px solid #e5e7eb",
                        borderRadius: "8px",
                        padding: "8px 16px",
                        fontSize: "12px",
                        fontWeight: "600",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                      }}
                    >
                      <i className="fa fa-times" style={{ marginRight: "4px" }}></i>
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              <div
                style={{ background: "#ffffff", borderRadius: "12px", border: "1px solid #e5e7eb", overflow: "hidden" }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "12px 16px",
                    background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                    borderBottom: "2px solid #e5e7eb",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <div
                      style={{
                        width: "36px",
                        height: "36px",
                        borderRadius: "8px",
                        background: `linear-gradient(135deg, ${COLORS.primary_color_shade5 || "#3b82f6"} 0%, ${COLORS.primary_color_shade5 || "#2563eb"} 100%)`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#fff",
                        fontSize: "16px",
                      }}
                    >
                      <i className="fa fa-list-alt"></i>
                    </div>
                    <div>
                      <h3 style={{ margin: 0, fontSize: "15px", fontWeight: "700", color: "#111827" }}>Social Value Fields</h3>
                      <p style={{ margin: "2px 0 0 0", fontSize: "11px", color: "#6b7280", fontWeight: "400" }}>
                        {fields.length} field{fields.length !== 1 ? "s" : ""} configured
                      </p>
                    </div>
                  </div>
                  {!showAddForm && (
                    <button
                      onClick={() => setShowAddForm(true)}
                      style={{
                        background: `linear-gradient(135deg, ${COLORS.primary_color_shade5 || "#3b82f6"} 0%, ${COLORS.primary_color_shade5 || "#2563eb"} 100%)`,
                        color: "#fff",
                        border: "none",
                        borderRadius: "8px",
                        padding: "6px 12px",
                        fontSize: "12px",
                        fontWeight: "600",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        boxShadow: "0 4px 6px rgba(59, 130, 246, 0.3)",
                      }}
                    >
                      <i className="fa fa-plus-circle"></i>
                      Add Field
                    </button>
                  )}
                </div>

                <div style={{ padding: "12px" }}>
                  {fields.length === 0 && !loading ? (
                    <div style={{ textAlign: "center", padding: "40px 20px", color: "#6b7280" }}>
                      <div
                        style={{
                          width: "60px",
                          height: "60px",
                          borderRadius: "50%",
                          background: "linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          margin: "0 auto 12px",
                          fontSize: "28px",
                          color: "#9ca3af",
                        }}
                      >
                        <i className="fa fa-inbox"></i>
                      </div>
                      <p style={{ margin: 0, fontSize: "14px", fontWeight: "600", color: "#111827" }}>
                        No social value fields found
                      </p>
                      <p style={{ margin: "6px 0 0 0", fontSize: "12px", color: "#6b7280" }}>
                        Click "Add Field" to create your first field
                      </p>
                    </div>
                  ) : (
                    <div style={{ overflowX: "auto", borderRadius: "8px" }}>
                      <table className="table" style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, margin: 0 }}>
                        <thead>
                          <tr style={{ background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)" }}>
                            <th style={{ padding: "10px 12px", textAlign: "left", fontSize: "11px", fontWeight: "700", color: "#475569", width: "50px", textTransform: "uppercase", letterSpacing: "0.3px", borderBottom: "2px solid #e2e8f0" }}>#</th>
                            <th style={{ padding: "10px 12px", textAlign: "center", fontSize: "11px", fontWeight: "700", color: "#475569", width: "50px", textTransform: "uppercase", letterSpacing: "0.3px", borderBottom: "2px solid #e2e8f0" }}>
                              <i className="fa fa-arrows-alt"></i>
                            </th>
                            <th style={{ padding: "10px 12px", textAlign: "left", fontSize: "11px", fontWeight: "700", color: "#475569", textTransform: "uppercase", letterSpacing: "0.3px", borderBottom: "2px solid #e2e8f0" }}>Field Name</th>
                            <th style={{ padding: "10px 12px", textAlign: "center", fontSize: "11px", fontWeight: "700", color: "#475569", width: "120px", textTransform: "uppercase", letterSpacing: "0.3px", borderBottom: "2px solid #e2e8f0" }}>Status</th>
                            <th style={{ padding: "10px 12px", textAlign: "center", fontSize: "11px", fontWeight: "700", color: "#475569", width: "180px", textTransform: "uppercase", letterSpacing: "0.3px", borderBottom: "2px solid #e2e8f0" }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {fields.map((field, index) => (
                            <tr
                              key={field.field_id}
                              draggable={!editingField || String(editingField.field_id) !== String(field.field_id)}
                              onDragStart={() => handleDragStart(index)}
                              onDragOver={handleDragOver}
                              onDrop={(e) => handleDrop(e, index)}
                              onDragEnd={handleDragEnd}
                              style={{
                                background: editingField && String(editingField.field_id) === String(field.field_id) ? "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)" : "#ffffff",
                                borderBottom: "1px solid #f1f5f9",
                                cursor: "move",
                                opacity: draggedIndex === index ? 0.5 : 1,
                                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                              }}
                            >
                              <td style={{ padding: "10px 12px", fontSize: "13px", color: "#64748b", fontWeight: "600" }}>
                                <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "26px", height: "26px", borderRadius: "6px", background: "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)", color: "#475569" }}>
                                  {index + 1}
                                </span>
                              </td>
                              <td style={{ padding: "10px 12px", textAlign: "center" }}>
                                <i className="fa fa-grip-vertical" style={{ color: "#94a3b8", cursor: "grab", fontSize: "14px" }}></i>
                              </td>
                              <td style={{ padding: "10px 12px" }}>
                                {editingField && String(editingField.field_id) === String(field.field_id) ? (
                                  <input
                                    type="text"
                                    value={editFieldName}
                                    onChange={(e) => setEditFieldName(e.target.value)}
                                    style={{ width: "100%", padding: "6px 10px", border: "2px solid #3b82f6", borderRadius: "6px", fontSize: "12px", background: "#ffffff", outline: "none", boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)", transition: "all 0.2s ease" }}
                                    onKeyPress={(e) => { if (e.key === "Enter") { e.preventDefault(); handleUpdateField(); } }}
                                    autoFocus
                                  />
                                ) : (
                                  <span style={{ fontSize: "13px", fontWeight: "600", color: "#111827" }}>{field.field_name}</span>
                                )}
                              </td>
                              <td style={{ padding: "10px 12px", textAlign: "center" }}>
                                <span
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    padding: "4px 10px",
                                    borderRadius: "16px",
                                    fontSize: "10px",
                                    fontWeight: "700",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.3px",
                                    background: (field.status || "Active").toUpperCase() === "ACTIVE" ? "linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)" : "linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)",
                                    color: (field.status || "Active").toUpperCase() === "ACTIVE" ? "#065f46" : "#991b1b",
                                  }}
                                >
                                  <i className={`fa fa-${(field.status || "Active").toUpperCase() === "ACTIVE" ? "check-circle" : "times-circle"}`} style={{ fontSize: "10px" }}></i>
                                  {field.status || "Active"}
                                </span>
                              </td>
                              <td style={{ padding: "10px 12px", textAlign: "center" }}>
                                {editingField && String(editingField.field_id) === String(field.field_id) ? (
                                  <div style={{ display: "flex", gap: "6px", justifyContent: "center" }}>
                                    <button
                                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleUpdateField(); }}
                                      disabled={saving}
                                      style={{ background: saving ? "#9ca3af" : "linear-gradient(135deg, #10b981 0%, #059669 100%)", color: "#fff", border: "none", borderRadius: "6px", padding: "6px 12px", fontSize: "11px", fontWeight: "600", cursor: saving ? "not-allowed" : "pointer" }}
                                    >
                                      {saving ? <><i className="fa fa-spinner fa-spin"></i> Saving...</> : <><i className="fa fa-check"></i> Save</>}
                                    </button>
                                    <button
                                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleCancelEdit(); }}
                                      style={{ background: "#ffffff", color: "#6b7280", border: "2px solid #e5e7eb", borderRadius: "6px", padding: "6px 12px", fontSize: "11px", fontWeight: "600", cursor: "pointer" }}
                                    >
                                      <i className="fa fa-times"></i> Cancel
                                    </button>
                                  </div>
                                ) : (
                                  <div style={{ display: "flex", gap: "6px", justifyContent: "center" }}>
                                    <button
                                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleEditField(field); }}
                                      style={{ background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)", color: "#fff", border: "none", borderRadius: "6px", padding: "6px 12px", fontSize: "11px", fontWeight: "600", cursor: "pointer" }}
                                    >
                                      <i className="fa fa-edit"></i> Edit
                                    </button>
                                    <button
                                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDeleteField(field); }}
                                      disabled={saving}
                                      style={{ background: saving ? "#9ca3af" : "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)", color: "#fff", border: "none", borderRadius: "6px", padding: "6px 12px", fontSize: "11px", fontWeight: "600", cursor: saving ? "not-allowed" : "pointer" }}
                                    >
                                      <i className="fa fa-trash"></i> Delete
                                    </button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {updatingOrder && (
                  <div style={{ textAlign: "center", padding: "10px", background: "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)", borderTop: "2px solid #e0f2fe", color: "#0369a1", fontSize: "12px", fontWeight: "600", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                    <i className="fa fa-spinner fa-spin"></i>
                    Updating field order...
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageSocialValueField;

